import React, { useEffect, useMemo, useState } from 'react'
import {
  Paper,
  Typography,
  Box,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  debounce,
  SelectChangeEvent,
} from '@mui/material'

import SaveIcon from '@mui/icons-material/Save'
import { useTranslation } from 'react-i18next'
import LoadingButton from '@mui/lab/LoadingButton'
import { convertContactOrderStatusTextToId } from '~/src/utils/convert'
import { Square } from '@mui/icons-material'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import useSWR from 'swr'
import { orderRepository, otherRepository, userRepository } from '~/src/repositories'
import { format } from 'date-fns'
import { UseFormDesktopDatePicker } from '~/src/components/molecules/UseForm/UseFormDesktopDatePicker/UseFormDesktopDatePicker'
import { UseFormSelectContactOrderStatus } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectContactOrderStatus'
import { UseFormSelectDrive2 } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectDrive2'
import { UseFormSelectTransmission } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectTransmission'
import { UseFormSelectDrive1 } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectDrive1'
import { UseFormSelectSupplyType } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectSupplyType'
import { UseFormSelectCatalyst } from '~/src/components/molecules/UseForm/UseFormSelect/UseFormSelectCatalyst'
import { OTHER_PARTS_PRODUCT_CODE } from '~/src/constants/requestOrder'

type TProduct = {
  controlNo: string
  categoryName: string
  partsCode: string
  freeInputProductName: string
  orderDetail: string
  manufacturer: string
  carName: string
  vehicleModel: string
  engineModel: string
  year: string
  originalRemark: string
  remark: string
  status: string
  drive1: string
  transmission: string
  drive2: string
  customerId: number
  supplyType: string
  supplier: string
  genuinePartNumber: string
  type: string
  engineDetail: string
}
interface IContactOrderCreateFormProps {
  loading: boolean
  data?: TProduct
  onSubmit: () => void
}
export const ContactOrderCreateForm: React.FC<IContactOrderCreateFormProps> = ({
  loading,
  data,
  onSubmit,
}): JSX.Element => {
  const { t } = useTranslation()

  const { data: products } = useSWR(['/products'], () =>
    otherRepository.getProducts(),
    {
      fallbackData: [],
    }
  )

  const { data: makers } = useSWR(['/request-orders/makers'], () => 
    orderRepository.requestOrderMakers(),
    {
      fallbackData: [],
    }
  )

  const [searchCarNamesParams, setSearchCarNamesParams] = useState<string>('')
  const debouncedCarName = useMemo(
    () => debounce((carName: string) => setSearchCarNamesParams(carName), 250), []
  )
  const { data: carNames, isValidating: isSeachCarNamesValidating } = useSWR(['/request-orders/car-names', searchCarNamesParams], () =>
    searchCarNamesParams ? orderRepository.requestOrderCarNames(searchCarNamesParams) : [],
    {
      fallbackData: [],
    }
  )

  const [searchModelCodesParams, setSearchModelCodesParams] = useState<string>('')
  const debouncedModelCode = useMemo(
    () => debounce((modelCode: string) => setSearchModelCodesParams(modelCode), 250), []
  )
  const { data: modelCodes, isValidating: isSeachModelCodesValidating } = useSWR(['/request-orders/model-codes', searchModelCodesParams], () =>
    searchModelCodesParams ? orderRepository.requestOrderModelCodes(searchModelCodesParams) : [],
    {
      fallbackData: [],
    }
  )

  const [searchEngineModelsParams, setSearchEngineModelsParams] = useState<string>('')
  const debouncedEngineModel = useMemo(
    () => debounce((engineModel: string) => setSearchEngineModelsParams(engineModel), 250), []
  )
  const { data: engineModels, isValidating: isSeachEngineModelsValidating } = useSWR(['/request-orders/engine-models', searchEngineModelsParams], () =>
  searchEngineModelsParams ? orderRepository.requestOrderEngineModels(searchEngineModelsParams) : [],
    {
      fallbackData: [],
    }
  )

  const [disabledFreeInputProductName, setDisabledFreeInputProductName] = useState<boolean>(true)
  const handleProductCodeChange = (event: SelectChangeEvent, value: string) => {
    if (value === OTHER_PARTS_PRODUCT_CODE) {
        setDisabledFreeInputProductName(false)
    } else {
        setValue('freeInputProductName', null)
        setDisabledFreeInputProductName(true)
    }
    setValue('partsCode', value);
  }

  const params = { isSigned: 1, limit: 100000 };
  const { data: userList } = useSWR(['/users', params], () =>
    userRepository.getUser(params),
    {
      fallbackData: { users: [] },
    }
  )

  const productSchema = yup.object().shape({
    controlNo: yup.number()
      .typeError(t('contact_order_detail.only_half-width_numbers_can_be_entered'))
      .nullable()
      .transform((value, original) => String(original).trim() === '' ? null : value),
    partsCode: yup.string()
      .nullable()
      .required(t('contact_order_detail.required_field')),
    freeInputProductName: yup.string()
      .nullable()
      .max(50, t('contact_order_detail.please_enter_within_n_text', {number: 50}))
      .when('partsCode', {
        is: (partsCode) => partsCode === OTHER_PARTS_PRODUCT_CODE,
        then: yup.string().nullable().required(t('contact_order_detail.required_field')),
      }),
    year: yup.number()
      .typeError(t('contact_order_detail.only_half-width_numbers_can_be_entered'))
      .lessThan(9999, t('contact_order_detail.must_be_less_than_n', {number: 9999}))
      .nullable()
      .transform((value, original) => String(original).trim() === '' ? null : value),
    engineDetail: yup.string()
      .max(50, t('contact_order_detail.please_enter_within_n_text', {number: 50})),
    genuinePartNumber: yup.string()
      .max(20, t('contact_order_detail.please_enter_within_n_single-byte_numbers', {number: 20})),
    orderDetail: yup.string()
      .max(1000, t('contact_order_detail.please_enter_within_n_text', {number: 1000})),
    customerId: yup.string()
      .nullable()
      .required(t('contact_order_detail.required_field')),
    orderDate: yup.date()
      .typeError(t('contact_order_detail.invalid_date_format'))
      .nullable(),
    arrivalDate: yup.date()
      .typeError(t('contact_order_detail.invalid_date_format'))
      .nullable(),
    supplier: yup.string()
      .nullable()
      .max(30, t('contact_order_detail.please_enter_within_n_text', {number: 30})),
    remark: yup.string()
      .max(1000, t('contact_order_detail.please_enter_within_n_text', {number: 1000})),
  })

  const { control, register, handleSubmit, reset, getValues, setValue, formState: {errors} } = useForm<TProduct>({
    mode: 'onChange',
    resolver: yupResolver(productSchema),
    defaultValues: data,
  })

  useEffect(() => {
    reset(data)
  }, [data])

  const submit: SubmitHandler<TProduct> = (input) => {
    onSubmit({
      refNo: input.controlNo ? String(input.controlNo) : null,
      partsCode: input.partsCode,
      freeInputProductName: input.freeInputProductName,
      manufacturer: input.manufacturer,
      carName: input.carName,
      vehicleModel: input.vehicleModel,
      engineModel: input.engineModel,
      drive1: input.drive1,
      transmission: input.transmission,
      drive2: input.drive2,
      year: input.year,
      orderDetail: input.orderDetail,
      customerId: input.customerId,
      supplyType: input.supplyType === '-' ? '' : input.supplyType,
      orderDate: input.orderDate === null ? '' : format(new Date(input.orderDate), 'yyyy-MM-dd'),
      arrivalDate: input.arrivalDate === null ? '' : format(new Date(input.arrivalDate), 'yyyy-MM-dd'),
      supplier: input.supplier,
      remark: input.remark,
      genuinePartNumber: input.genuinePartNumber,
      engineDetail: input.engineDetail,
      withCatalyst: input.withCatalyst === '' ? null : input.withCatalyst,
    })
  }

  return (
    <Paper>
      <form onSubmit={handleSubmit(submit)}>
        <Box display={'flex'} >
          <Typography
            fontFamily={'Roboto'}
            variant={'h2'}
            padding={2}
            fontWeight={'medium'}
            fontStyle={'normal'}
            fontSize={20}
            lineHeight={'32px'}
            letterSpacing={'0.15px'}
            color={'#000000'}
          >
            {t('contact_order_detail.product_details')}
          </Typography>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid rgba(224, 224, 224, 1)',
            padding: '32px 16px',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <Grid container spacing={2}>
            <Grid item lg={6}>
              <Box marginBottom={2}>
                <Typography
                  fontFamily={'Roboto'}
                  variant={'h2'}
                  fontWeight={'medium'}
                  fontStyle={'normal'}
                  fontSize={18}
                  lineHeight={'32px'}
                  letterSpacing={'0.15px'}
                  color={'#000000'}
                  sx={{
                    padding: '0 16px 16px 16px',
                  }}
                >
                  <Square color="action" sx={{fontSize: 14}}/> {t('contact_order_detail.title_product_info')}
                </Typography>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.control_no')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <TextField
                      {...register('controlNo')}
                      size={'small'}
                      inputProps={{style: {fontSize: 14}}}
                      error={'controlNo' in errors}
                      helperText={errors.controlNo?.message}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.product_code')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="partsCode"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 320 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={products.map((product) => product.code)}
                            getOptionLabel={(option) => {
                              const product = products?.find(({code, name}) => code === option)
                              return product ? product.code + ' (' + product.name + ')' : ''
                            }}
                            renderInput={(params) =>
                              <TextField
                                {...params}
                                size='small'
                                inputProps = {{...params.inputProps, style: { fontSize: 14 }}}
                                error={'partsCode' in errors}
                                helperText={errors.partsCode?.message}
                              />
                            }
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={handleProductCodeChange}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                        <Typography
                        fontFamily={'Roboto'}
                        fontWeight={'medium'}
                        fontStyle={'normal'}
                        fontSize={14}
                        lineHeight={'24px'}
                        letterSpacing={'0.17px'}
                        color={'#000000'}
                        >
                        {t('contact_order_detail.free_input_product_name')}
                        </Typography>
                    </Box>
                    <Box width={'532px'}>
                        <TextField
                            {...register('freeInputProductName')}
                            size={'small'}
                            inputProps={{style: {fontSize: 14}}}
                            error={'freeInputProductName' in errors}
                            helperText={errors.freeInputProductName?.message}
                            fullWidth
                            disabled={disabledFreeInputProductName}
                            placeholder={t('contact_order_detail.free_input_only', {otherPartsProductCode: OTHER_PARTS_PRODUCT_CODE})}
                        />
                    </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.manufacturer')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="manufacturer"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 200 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={makers}
                            renderInput={(params) => <TextField {...params} size='small' inputProps = {{...params.inputProps, style: { fontSize: 14 }}}  />}
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={(event, newValue) => {
                              setValue("manufacturer", newValue);
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.car_name')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="carName"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 200 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={carNames}
                            loading={isSeachCarNamesValidating}
                            forcePopupIcon={false}
                            renderInput={(params) => <TextField {...params} size='small' inputProps = {{...params.inputProps, style: { fontSize: 14 }}}  />}
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={(event, newValue) => {
                              setValue("carName", newValue);
                            }}
                            onInputChange={(e, newValue) => {
                              debouncedCarName(newValue)
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.vehicle_model')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="vehicleModel"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 200 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={modelCodes}
                            loading={isSeachModelCodesValidating}
                            forcePopupIcon={false}
                            renderInput={(params) => <TextField {...params} size='small' inputProps = {{...params.inputProps, style: { fontSize: 14 }}}  />}
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={(event, newValue) => {
                              setValue("vehicleModel", newValue);
                            }}
                            onInputChange={(e, newValue) => {
                              debouncedModelCode(newValue)
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.engine_model')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="engineModel"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 200 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={engineModels}
                            loading={isSeachEngineModelsValidating}
                            forcePopupIcon={false}
                            renderInput={(params) => <TextField {...params} size='small' inputProps = {{...params.inputProps, style: { fontSize: 14 }}}  />}
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={(event, newValue) => {
                              setValue("engineModel", newValue);
                            }}
                            onInputChange={(e, newValue) => {
                              debouncedEngineModel(newValue)
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.drive_1_/_tm_/_drive_2')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <UseFormSelectDrive1
                      control={control}
                      name="drive1"
                    />
                    <UseFormSelectTransmission
                      control={control}
                      name="transmission"
                      sx={{
                        marginLeft: '20px'
                      }}
                    />
                    <UseFormSelectDrive2
                      control={control}
                      name="drive2"
                      sx={{
                        marginLeft: '20px'
                      }}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.year')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <TextField
                      {...register('year')}
                      size={'small'}
                      inputProps={{style: {fontSize: 14}}}
                      error={'year' in errors}
                      helperText={errors.year?.message}
                      sx={{width: 100}}
                    />
                  </Box>
                </Stack>

                {data?.type !== 'STOCK' &&
                  <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                      <Typography
                        fontFamily={'Roboto'}
                        fontWeight={'medium'}
                        fontStyle={'normal'}
                        fontSize={14}
                        lineHeight={'24px'}
                        letterSpacing={'0.17px'}
                        color={'#000000'}
                      >
                        {t('contact_order_detail.engine_detail')}
                      </Typography>
                    </Box>
                    <Box width={'532px'}>
                      <TextField
                        {...register('engineDetail')}
                        size={'small'}
                        inputProps={{style: {fontSize: 14}}}
                        error={'engineDetail' in errors}
                        helperText={errors.engineDetail?.message}
                        fullWidth
                      />
                    </Box>
                  </Stack>
                }

                {data?.type === 'STOCK' &&
                  <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                      <Typography
                        fontFamily={'Roboto'}
                        fontWeight={'medium'}
                        fontStyle={'normal'}
                        fontSize={14}
                        lineHeight={'24px'}
                        letterSpacing={'0.17px'}
                        color={'#000000'}
                      >
                        {data?.categoryName === 'Engine'
                          ? t('contact_order_detail.engine_detail')
                          : t('contact_order_detail.accident_detail')}
                      </Typography>
                    </Box>
                    <Box width={'532px'}>
                      <Typography
                        fontFamily={'Roboto'}
                        fontWeight={'regular'}
                        fontStyle={'normal'}
                        lineHeight={'24px'}
                        fontSize={14}
                        letterSpacing={'0.15px'}
                        color={'#000000'}
                      >
                        {data?.originalRemark}
                      </Typography>
                    </Box>
                  </Stack>
                }

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.catalyst')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <UseFormSelectCatalyst
                      control={control}
                      name="withCatalyst"
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.parts_number')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <TextField
                      {...register('genuinePartNumber')}
                      size={'small'}
                      inputProps={{style: {fontSize: 14}}}
                      error={'genuinePartNumber' in errors}
                      helperText={errors.genuinePartNumber?.message}
                      sx={{width: 220}}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.order_details')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <TextField
                      {...register('orderDetail')}
                      multiline
                      rows={4}
                      fullWidth
                      size={'small'}
                      inputProps={{style: {fontSize: 14}}}
                      error={'orderDetail' in errors}
                      helperText={errors.orderDetail?.message}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>

            <Grid item lg={6}>
              <Box marginBottom={2}>
                <Typography
                  fontFamily={'Roboto'}
                  variant={'h2'}
                  fontWeight={'medium'}
                  fontStyle={'normal'}
                  fontSize={18}
                  lineHeight={'32px'}
                  letterSpacing={'0.15px'}
                  color={'#000000'}
                  sx={{
                    padding: '0 16px 16px 16px',
                  }}
                >
                  <Square color="action" sx={{fontSize: 14}}/> {t('contact_order_detail.title_customer_info')}
                </Typography>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.customer_code')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <Controller
                      name="customerId"
                      control={control}
                      render={({field}) => (
                        <FormControl size={'small'} sx={{ minWidth: 320 }}>
                          <Autocomplete
                            ListboxProps={{style: {fontSize: 14}}}
                            options={userList.users.map((user) => user.id)}
                            getOptionLabel={(option) => {
                              const user = userList.users?.find(({id, name}) => id === option)
                              return user ? user.customerCode + ' (' + user.companyName + ')' : ''
                            }}
                            renderOption={(props, option) =>
                              <Box component="li" {...props} key={option}>
                                {props.key}
                              </Box>
                            }
                            renderInput={(params) =>
                              <TextField
                                {...params}
                                size='small'
                                inputProps = {{...params.inputProps, style: { fontSize: 14 }}}
                                error={'customerId' in errors}
                                helperText={errors.customerId?.message}
                              />
                            }
                            name={field.name}
                            ref={field.ref}
                            value={field.value}
                            onChange={(event, newValue) => {
                              setValue("customerId", newValue);
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </Stack>
              </Box>

              <Box marginBottom={2}>
                <Typography
                  fontFamily={'Roboto'}
                  variant={'h2'}
                  fontWeight={'medium'}
                  fontStyle={'normal'}
                  fontSize={18}
                  lineHeight={'32px'}
                  letterSpacing={'0.15px'}
                  color={'#000000'}
                  sx={{
                    padding: '0 16px 16px 16px',
                  }}
                >
                  <Square color="action" sx={{fontSize: 14}}/> {t('contact_order_detail.title_order_info')}
                </Typography>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.supply_type')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <UseFormSelectSupplyType
                      control={control}
                      name="supplyType"
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.order_date')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <UseFormDesktopDatePicker
                      control={control}
                      name="orderDate"
                      label={t('contact_order_detail.order_date')}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.arrival_date')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <UseFormDesktopDatePicker
                      control={control}
                      name="arrivalDate"
                      label={t('contact_order_detail.arrival_date')}
                    />
                  </Box>
                </Stack>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.supplier')}
                    </Typography>
                  </Box>
                  <Box width={'532px'}>
                    <TextField
                        {...register('supplier')}
                        size={'small'}
                        inputProps={{style: {fontSize: 14}}}
                        error={'supplier' in errors}
                        helperText={errors.supplier?.message}
                        sx={{width: 300}}
                      />
                  </Box>
                </Stack>
              </Box>

              <Box marginBottom={2}>
                <Typography
                  fontFamily={'Roboto'}
                  variant={'h2'}
                  fontWeight={'medium'}
                  fontStyle={'normal'}
                  fontSize={18}
                  lineHeight={'32px'}
                  letterSpacing={'0.15px'}
                  color={'#000000'}
                  sx={{
                    padding: '0 16px 16px 16px',
                  }}
                >
                  <Square color="action" sx={{fontSize: 14}}/> {t('contact_order_detail.title_other_info')}
                </Typography>

                <Stack direction={'row'} spacing={2} sx={{ pb: 2 }}>
                  <Box display={'flex'} justifyContent={'flex-end'} width={'160px'}>
                    <Typography
                      fontFamily={'Roboto'}
                      fontWeight={'medium'}
                      fontStyle={'normal'}
                      fontSize={14}
                      lineHeight={'24px'}
                      letterSpacing={'0.17px'}
                      color={'#000000'}
                    >
                      {t('contact_order_detail.remarks')}
                    </Typography>
                  </Box>
                  <TextField
                    sx={{
                      '& .MuiInputBase-root': {
                        padding: '9px 12px',
                        fontSize: 16,
                        fontWeight: 'regular',
                        letterSpacing: '0.15px',
                        fontStyle: 'normal',
                        lineHeight: '24px',
                        fontFamily: 'Roboto',
                        color: '#000000',
                      },
                      width: '532px',
                    }}
                    multiline
                    rows={4}
                    {...register('remark')}
                    size={'small'}
                    inputProps={{style: {fontSize: 14}}}
                    error={'remark' in errors}
                    helperText={errors.remark?.message}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box display={'flex'} justifyContent={'flex-end'} sx={{ padding: 2 }}>
          <LoadingButton
            color="primary"
            type="submit"
            loading={loading}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
            size={'medium'}
            sx={{ fontSize: '15px', color: 'white', background: '#3F51B5' }}
          >
            {t('contact_order_detail.save')}
          </LoadingButton>
        </Box>
      </form>
    </Paper>
  )
}
